import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { RefreshCw, Bluetooth, Check } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { canPrint } from '../hooks/usePrintOrder'
import { isAutoPrintEnabled, setAutoPrintEnabled } from './AutoPrinter'

interface BluetoothDevice {
  name: string
  address: string
}

interface PrinterSettingsDialogProps {
  isOpen: boolean
  onClose: () => void
}

export function PrinterSettingsDialog({ isOpen, onClose }: PrinterSettingsDialogProps) {
  const { t } = useTranslation('orders')
  const [devices, setDevices] = useState<BluetoothDevice[]>([])
  const [isScanning, setIsScanning] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState(
    () => localStorage.getItem('printer_device_address') ?? '',
  )
  const [copies, setCopies] = useState(
    () => parseInt(localStorage.getItem('printer_copies') ?? '1', 10),
  )
  const [autoPrint, setAutoPrint] = useState(isAutoPrintEnabled)

  // Load paired devices when dialog opens
  useEffect(() => {
    if (isOpen && canPrint) {
      void scanDevices()
    }
  }, [isOpen])

  const scanDevices = async () => {
    if (!canPrint) return
    setIsScanning(true)
    try {
      const { EscPosPrinter } = await import('@/plugins/escpos-printer')
      const result = await EscPosPrinter.listPairedDevices()
      setDevices(result.devices)
    } catch {
      setDevices([])
    } finally {
      setIsScanning(false)
    }
  }

  const handleSelectDevice = (address: string) => {
    setSelectedAddress(address)
    localStorage.setItem('printer_device_address', address)
  }

  const handleCopiesChange = (value: string) => {
    const num = Math.max(1, Math.min(9, parseInt(value, 10) || 1))
    setCopies(num)
    localStorage.setItem('printer_copies', String(num))
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bluetooth className="h-5 w-5" />
            {t('printer.settingsTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Paired devices */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>{t('printer.pairedDevices')}</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void scanDevices()}
                disabled={isScanning}
              >
                <RefreshCw className={`h-4 w-4 me-1 ${isScanning ? 'animate-spin' : ''}`} />
                {t('printer.refresh')}
              </Button>
            </div>

            <div className="space-y-2 max-h-48 overflow-y-auto">
              {devices.length === 0 && !isScanning && (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  {t('printer.noDevices')}
                </p>
              )}
              {devices.map((device) => (
                <button
                  key={device.address}
                  onClick={() => handleSelectDevice(device.address)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-colors ${
                    selectedAddress === device.address
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  <div>
                    <p className="font-medium text-sm">{device.name}</p>
                    <p className="text-xs text-muted-foreground">{device.address}</p>
                  </div>
                  {selectedAddress === device.address && (
                    <Check className="h-4 w-4 text-primary shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Automatic printing of new orders (old RestaurantApp behaviour) */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="auto-print">{t('printer.autoPrint')}</Label>
              <p className="text-xs text-muted-foreground">{t('printer.autoPrintHint')}</p>
            </div>
            <Switch
              id="auto-print"
              checked={autoPrint}
              onCheckedChange={(checked) => {
                setAutoPrint(checked)
                setAutoPrintEnabled(checked)
              }}
            />
          </div>

          {/* Number of copies */}
          <div className="space-y-2">
            <Label htmlFor="copies">{t('printer.copies')}</Label>
            <Input
              id="copies"
              type="number"
              min={1}
              max={9}
              value={copies}
              onChange={(e) => handleCopiesChange(e.target.value)}
              className="w-24"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
